import * as turf from "@turf/turf";

import {
  computePolygonMetrics,
  cloneFeatureCollection,
} from "../polygonDraw/helpers";

export const DEFAULT_HISTORY_FILTERS = {
  creationTimeStart: "",
  creationTimeEnd: "",
  fireRiskRange: [0, 1],
  floodRiskRange: [0, 1],
  earthquakeRiskRange: [0, 1],
};

export const DEFAULT_HISTORY_ORDER = "creation_time:desc";

const toDayBoundaryIso = (dateValue, boundary) => {
  if (!dateValue) return undefined;

  const timeSuffix = boundary === "start" ? "T00:00:00.000Z" : "T23:59:59.999Z";
  const iso = new Date(`${dateValue}${timeSuffix}`).toISOString();

  return Number.isNaN(new Date(iso).getTime()) ? undefined : iso;
};

export const buildHistoryParams = ({
  search,
  orderCombined = DEFAULT_HISTORY_ORDER,
  filters = DEFAULT_HISTORY_FILTERS,
}) => {
  const [orderBy = "creation_time", orderDir = "desc"] = (
    orderCombined || DEFAULT_HISTORY_ORDER
  ).split(":");

  const start = toDayBoundaryIso(filters.creationTimeStart, "start");
  const end = toDayBoundaryIso(filters.creationTimeEnd, "end");

  const [fireRiskStart, fireRiskEnd] = filters.fireRiskRange || [0, 1];
  const [floodRiskStart, floodRiskEnd] = filters.floodRiskRange || [0, 1];
  const [earthquakeRiskStart, earthquakeRiskEnd] =
    filters.earthquakeRiskRange || [0, 1];

  return {
    search: search || undefined,
    orderBy,
    orderDir,
    creationTimeStart: start || undefined,
    creationTimeEnd: end || undefined,
    fireRiskStart: fireRiskStart > 0 ? fireRiskStart : undefined,
    fireRiskEnd: fireRiskEnd < 1 ? fireRiskEnd : undefined,
    floodRiskStart: floodRiskStart > 0 ? floodRiskStart : undefined,
    floodRiskEnd: floodRiskEnd < 1 ? floodRiskEnd : undefined,
    earthquakeRiskStart:
      earthquakeRiskStart > 0 ? earthquakeRiskStart : undefined,
    earthquakeRiskEnd: earthquakeRiskEnd < 1 ? earthquakeRiskEnd : undefined,
  };
};

export const hasValidCoordinates = (assessment) => {
  const lat = Number(assessment?.latitude);
  const lng = Number(assessment?.longitude);
  return Number.isFinite(lat) && Number.isFinite(lng);
};

export const toMarkerInfo = (assessment) => {
  if (!assessment) return null;

  return {
    assessmentId: assessment.id,
    address: assessment.address || "",
    open: true,
    riskData: {
      id: assessment.id,
      latitude: Number(assessment.latitude),
      longitude: Number(assessment.longitude),
      egsa87x: Number(assessment.egsa87x),
      egsa87y: Number(assessment.egsa87y),
      fire: assessment.fire || {},
      flood: assessment.flood || {},
      earthquake: assessment.earthquake || {},
    },
  };
};

// --- Area (polygon) assessments -------------------------------------------

export const isAreaAssessment = (assessment) =>
  assessment?.assessment_type === "area" || Boolean(assessment?.geometry);

// Normalize the stored geometry into a GeoJSON FeatureCollection.
export const getAssessmentGeometry = (assessment) => {
  const geometry = assessment?.geometry;
  if (!geometry) return null;
  if (geometry.type === "FeatureCollection") {
    return cloneFeatureCollection(geometry);
  }
  if (geometry.type === "Feature") {
    return { type: "FeatureCollection", features: [geometry] };
  }
  // Bare geometry (Polygon/MultiPolygon).
  return {
    type: "FeatureCollection",
    features: [{ type: "Feature", properties: {}, geometry }],
  };
};

// Per-polygon metrics — prefer the values persisted in each feature's
// `properties`, falling back to computing them from the geometry.
export const getAreaPolygonMetrics = (assessment) => {
  const fc = getAssessmentGeometry(assessment);
  if (!fc) return [];

  const derived = computePolygonMetrics(fc);
  return fc.features.map((feature, index) => {
    const props = feature.properties || {};
    const fallback = derived[index] || {};
    const area = Number(props.area_sqm);
    const perimeter = Number(props.perimeter_m);
    return {
      id: feature.id ?? index,
      area: Number.isFinite(area) ? area : fallback.area || 0,
      perimeter: Number.isFinite(perimeter) ? perimeter : fallback.perimeter || 0,
    };
  });
};

// Totals — prefer the stored columns, fall back to summing per-polygon metrics.
export const getAreaTotals = (assessment) => {
  const perPolygon = getAreaPolygonMetrics(assessment);
  const storedArea = Number(assessment?.area_sqm);
  const storedPerimeter = Number(assessment?.perimeter_m);

  const area = Number.isFinite(storedArea)
    ? storedArea
    : perPolygon.reduce((sum, m) => sum + (m.area || 0), 0);
  const perimeter = Number.isFinite(storedPerimeter)
    ? storedPerimeter
    : perPolygon.reduce((sum, m) => sum + (m.perimeter || 0), 0);

  return { area, perimeter, count: perPolygon.length };
};

// [[minLng, minLat], [maxLng, maxLat]] for map.fitBounds.
export const getAreaBounds = (geometry) => {
  if (!geometry) return null;
  try {
    const [minX, minY, maxX, maxY] = turf.bbox(geometry);
    if (![minX, minY, maxX, maxY].every(Number.isFinite)) return null;
    return [
      [minX, minY],
      [maxX, maxY],
    ];
  } catch {
    return null;
  }
};
